import React from "react";
import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { ProductComponent, Classification, ReportingQuantity } from "../../graphql/graphql";
import { Collapsible } from "@nike/epic-react-ui";

export type ClassificationDetailsTableColumn<T> = ColumnDef<T, any> & {
  accessorKey: keyof T | string;
  header: string | React.ReactNode | (() => React.ReactNode);
};

export const getClassificationDetailsTableColumns = (): ClassificationDetailsTableColumn<ProductComponent>[] => [
    {
        header: () => <span title="HTS Code">HTS Code</span>,
        accessorKey: "classification_htsCode",
        cell: ({ row }) => {
            const classification = row.original.classification as Classification;
            if (!row.original.additionalClassifications || row.original.additionalClassifications.length === 0) {
                return (                
                    <span>
                        {classification?.code ?? "N/A"}
                    </span>
                );
            } else {
                return (
                    <Collapsible
                        title={classification?.code ?? "N/A"}
                        isOpen={true}
                    >
                        <div className="tree-list">
                            {row.original.additionalClassifications.map((curr, idx) => (
                                <div key={idx} className="tree-list-item">
                                    <span>{curr?.code ?? ""}</span>
                                    <span className="eds-spacing--ml-8">{curr?.type ?? ""}</span>
                                </div>
                            ))}
                        </div>
                    </Collapsible>
                );
            }
        }

    },
    {
        header: () => <span title="Description">Description</span>,
        accessorKey: "classification_description",
        size: 260,
        cell: ({ row }) => {
            const classification = row.original.classification as Classification;
            return (
                <span>
                    {classification?.descriptions?.find(desc => desc?.languageCode?.toUpperCase() === "EN")?.shortDescription ?? "N/A"}
                </span>
            );
        }
    },
    {
        header: () => <span title="Declaration Price">Declaration Price</span>,
        accessorKey: "price",
        cell: ({ row }) => {
            const hasCurrency = typeof row.original.currency === 'string' && row.original.currency !== '';
            const hasPrice = row.original.price !== undefined && row.original.price !== null;
            const formatted = hasCurrency && hasPrice
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: row.original.currency as string }).format(row.original.price)
                : undefined;
            const value = formatted ? formatted : "N/A";
            return (
                <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
                    {value}
                </span>
            );
        }
    },
    {
        header: () => <span title="Declaration Quantity">Declaration Qty</span>,
        accessorKey: "quantity",
        cell: ({ row }) => {
            const quantities = row.original.quantity as ReportingQuantity[] | undefined;
            if (!quantities || quantities.length === 0) {
              return <span className="eds-text-color--danger">N/A</span>;
            }
            return (
              <>
                {quantities.map((curr, idx) => {
                  const hasQuantity = curr?.quantity !== undefined && curr?.quantity !== null;
                  const hasUnit = !!curr?.reportingUnitOfMeasure;
                  if (!hasQuantity && !hasUnit) {
                    return (
                      <div key={idx} className="eds-text-color--danger">
                        N/A
                      </div>
                    );
                  }
                  return (
                    <div key={idx}>
                      {hasQuantity ? curr.quantity : ""}
                      {hasUnit ? curr.reportingUnitOfMeasure : ""}
                    </div>
                  );
                })}
              </>
            );
        }
    },
    {
        header: () => <span title="Net Net Weight">Net Net Weight (g)</span>,
        accessorKey: "netNetWeight",
        cell: ({ row }) => {
            const value = row.original.netNetWeight ?? "N/A";
            return (
                <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
                    {value}
                </span>
            );
        }
    },
    {
        header: () => <span title="Net Weight">Net Weight (g)</span>,
        accessorKey: "netWeight",
        cell: ({ row }) => {
            const value = row.original.netWeight ?? "N/A";
            return (
                <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
                    {value}
                </span>
            );
        }
    },
    {
        header: () => <span title="Gross Weight">Gross Weight (g)</span>,
        accessorKey: "grossWeight",
        cell: ({ row }) => {
            const value = row.original.grossWeight ?? "N/A";
            return (
                <span className={value === "N/A" ? "eds-text-color--danger" : ""}>
                    {value}
                </span>
            );
        }
    },
];
